<template>
  <FormDialog
    v-model="internalModel"
    title="Edit Profile Information"
    :loading="loading"
    :submit-disabled="!isFormValid"
    submit-label="Save Profile"
    @submit="handleSubmit"
  >
    <v-form ref="formRef" v-model="isFormValid">
      <p class="text-body-2 text-medium-emphasis mb-4">
        Update your profile information.
      </p>

      <FormTextField
        v-model="formData.givenName"
        label="First Name"
        required
        variant="outlined"
        density="comfortable"
        class="mb-2"
        :disabled="loading"
      />

      <FormTextField
        v-model="formData.surname"
        label="Last Name"
        required
        variant="outlined"
        density="comfortable"
        class="mb-2"
        :disabled="loading"
      />

      <FormTextField
        v-model="formData.jobTitle"
        label="Job Title"
        required
        variant="outlined"
        density="comfortable"
        :disabled="loading"
      />
    </v-form>
  </FormDialog>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { useAuthStore } from '../stores/authStore'
import { useNotificationStore } from '../stores/notificationStore'
import FormDialog from './shared/FormDialog.vue'
import FormTextField from './shared/FormTextField.vue'

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['update:modelValue', 'saved'])

const authStore = useAuthStore()
const notificationStore = useNotificationStore()

const formRef = ref(null)
const isFormValid = ref(false)
const loading = ref(false)

const formData = ref({
  givenName: '',
  surname: '',
  jobTitle: ''
})

const internalModel = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value)
})

// Watch for profile changes and populate form
watch(() => authStore.userProfile, (profile) => {
  if (profile) {
    formData.value = {
      givenName: profile.givenName || '',
      surname: profile.surname || '',
      jobTitle: profile.jobTitle || ''
    }
  }
}, { immediate: true })

const handleSubmit = async () => {
  // Validate form
  const { valid } = await formRef.value.validate()
  if (!valid) {
    notificationStore.showError('Please fill in all required fields')
    return
  }

  loading.value = true
  try {
    // Combine first name and last name to create display name
    const givenName = formData.value.givenName.trim()
    const surname = formData.value.surname.trim()

    // Build display name from non-empty name parts
    const nameParts = []
    if (givenName) nameParts.push(givenName)
    if (surname) nameParts.push(surname)
    const displayName = nameParts.join(' ')

    await authStore.updateUserProfile({
      givenName,
      surname,
      displayName,
      jobTitle: formData.value.jobTitle.trim()
    })

    notificationStore.showSuccess('Profile updated successfully')
    emit('saved')
    internalModel.value = false
  } catch (error) {
    notificationStore.showError(error.message || 'Failed to update profile')
  } finally {
    loading.value = false
  }
}

</script>
